import { type ReactNode } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useSuspenseQuery } from '@tanstack/react-query';

import { Page } from '@/components/layout/Page';
import { Section } from '@/components/layout/Section';
import { PageHeader } from '@/components/composed/PageHeader';
import {
  AgentBrokerDetail,
  agentBrokerDetailQuery,
  agentBrokerSections,
} from '@/features/agents-brokers';
import { usePageMeta } from '@/app/shell/use-page-meta';
import { useRailSubNav } from '@/app/shell/use-rail-sub-nav';
import { resolveSection } from '@/lib/entity-sections';
import { agentBrokerId } from '@/lib/api/ids';

export const Route = createFileRoute(
  '/_auth/agents-brokers/$agentBrokerId/$section',
)({
  loader: ({ context, params }) =>
    context.queryClient.ensureQueryData(
      agentBrokerDetailQuery(agentBrokerId(params.agentBrokerId)),
    ),
  component: AgentBrokerDetailPage,
});

/*
 * Injects a typed router <Link> into the domain-free RailSubNav. The rail sub-nav
 * builds the full `/agents-brokers/{id}/{section}` href; we pass it straight
 * through to `to` so every param survives — client navigation and deep-linking
 * both work.
 */
function renderLink(props: {
  href: string;
  children: ReactNode;
  className?: string;
  'aria-current'?: 'page';
  'data-section'?: string;
}) {
  const { href, children, ...rest } = props;
  return (
    <Link to={href} {...rest}>
      {children}
    </Link>
  );
}

function AgentBrokerDetailPage() {
  const { agentBrokerId: agentBrokerIdParam, section } = Route.useParams();
  const id = agentBrokerId(agentBrokerIdParam);
  const { data: agentBroker } = useSuspenseQuery(agentBrokerDetailQuery(id));
  const activeSectionId =
    resolveSection(section, agentBrokerSections) ?? agentBrokerSections[0]!.id;

  usePageMeta({ title: 'Agent / Broker' });
  useRailSubNav({
    sections: agentBrokerSections,
    activeSectionId,
    basePath: `/agents-brokers/${id}`,
    entityName: agentBroker.name,
    backHref: '/agents-brokers',
    backLabel: 'All Agents & Brokers',
    navLabel: 'Agent / Broker sections',
    renderLink,
  });

  return (
    <Page>
      <PageHeader title="Agent / Broker" />
      <Section>
        <AgentBrokerDetail agentBrokerId={id} section={section} />
      </Section>
    </Page>
  );
}
